import { useEffect, useState } from "react";
import axios from "axios";
import { Newspaper, ExternalLink, Loader2 } from "lucide-react";
import { useI18n } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ["general", "business", "agriculture", "travel", "health", "sports", "culture", "science"];

function timeAgo(pub) {
  const d = new Date(pub);
  if (isNaN(d)) return "";
  const h = Math.round((Date.now() - d.getTime()) / 3600000);
  if (h < 1) return "just now";
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

export default function CountryNews({ path, name }) {
  const { tr } = useI18n();
  const [category, setCategory] = useState("general");
  const [items, setItems] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setItems(null);
    setFailed(false);
    axios.get(`${API}${path}`, { params: { category } })
      .then((r) => setItems(r.data.items))
      .catch(() => setFailed(true));
  }, [path, category]);

  return (
    <section id="news" className="scroll-mt-32 mb-12" data-testid="country-news-section">
      <div className="flex items-center gap-3">
        <span className="w-9 h-9 rounded-xl bg-[#FEF2F2] flex items-center justify-center"><Newspaper size={17} className="text-[#DC2626]" /></span>
        <h2 className="text-xl font-extrabold text-[#0F2557] flex-1">{tr("Latest from")} {name}</h2>
        <a href="https://news.socialpulse.com" target="_blank" rel="noreferrer" data-testid="news-socialpulse-link"
          className="text-xs font-extrabold text-[#DC2626] inline-flex items-center gap-1.5 hover:underline">
          News.SocialPulse.com <ExternalLink size={11} />
        </a>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-4">
        {CATEGORIES.map((c) => (
          <button key={c} data-testid={`news-category-${c}`} onClick={() => setCategory(c)}
            className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors capitalize ${
              category === c ? "bg-[#DC2626] border-[#DC2626] text-white" : "bg-white border-[#E2E8F0] text-[#475569] hover:border-[#DC2626]"}`}>
            {tr(c.charAt(0).toUpperCase() + c.slice(1))}
          </button>
        ))}
      </div>
      <div className="mt-4 space-y-2.5" data-testid="news-list">
        {items === null && !failed && (
          <div className="flex items-center gap-2 text-[#DC2626] text-sm font-bold py-4"><Loader2 size={14} className="animate-spin" /> {tr("Fetching headlines…")}</div>
        )}
        {failed && <p className="text-sm text-[#64748B] py-4">{tr("News feed is unavailable right now — please try again shortly.")}</p>}
        {items?.slice(0, 8).map((n, i) => (
          <a key={i} href={n.url} target="_blank" rel="noreferrer" data-testid="news-headline"
            className="block border border-[#E5EDF7] rounded-2xl px-4 py-3 hover:border-[#DC2626] hover:bg-[#FFF9F9] transition-colors group">
            <div className="text-sm font-bold text-[#0F2557] group-hover:text-[#DC2626] leading-snug">{n.title}</div>
            <div className="text-[0.65rem] font-semibold text-[#94A3B8] mt-1">{n.source}{n.published ? ` · ${timeAgo(n.published)}` : ""}</div>
          </a>
        ))}
        {items?.length === 0 && <p className="text-sm text-[#64748B] py-4">{tr("No recent headlines for this category.")}</p>}
      </div>
      <p className="text-[0.58rem] text-[#94A3B8] mt-3">{tr("Live headlines aggregated for the Social Pulse network — links open the original publishers.")}</p>
    </section>
  );
}
